<template>
  <form id="authForm" class="signin-container m-t animated fadeInUp" action="#" @submit.prevent @keyup.enter="logIn">
    <v-layout row wrap class="mx-0">
      <!-- title -->
      <v-flex xs12 tag="h3">
        {{ $t("view.login.signin.title") }}
      </v-flex>

      <!-- Username -->
      <v-flex xs12>
        <v-text-field
          v-model.trim="auth.username"
          prepend-icon="person"
          name="username"
          :label="$t('view.login.signin.username.placeholder')"
          type="text"
          :loading="view.action.login.busy"
          :error-messages="$t(`validator.${getFirstError($v.auth.username)}.text`, '')" />
      </v-flex>

      <!-- Password -->
      <v-flex xs12>
        <v-text-field
          v-model.trim="auth.password"
          prepend-icon="lock"
          name="password"
          :loading="view.action.login.busy"
          :error-messages="getFirstErrorDescription($v.auth.password)"
          :label="$t('view.login.signin.password.placeholder')"
          :type="show ? 'text' : 'password'"
          :append-icon="show ? 'mdi-eye' : 'mdi-eye-off'"
          @click:append="show = !show" />
      </v-flex>

      <!-- ERROR MESSAGE -->
      <v-flex xs12 class="pb-5">
        <label v-if="view.elem.label.loginError.visible" class="error--text" for="loginSubmit">
          {{ loginErrorMessage }}
        </label>
      </v-flex>

      <!-- CAPTCHA -->
      <v-flex xs12>
        <v-layout v-show="view.elem.captcha.visible" row>
          <v-flex id="mc" xs12 style="min-height: 200px">
            <p style="margin: 0px 0 0px">Por favor dibuja la línea:</p>
            <canvas id="mc-canvas" />
          </v-flex>
          <input id="captchaResult" type="submit" style="height: 0px; padding: 0px 0px 0px 0px; margin: 0px 0px 0px 0px; border: 0px 0px 0px 0px" disabled="disabled" value="Submit Form" />
        </v-layout>
      </v-flex>

      <!-- SUBMIT BUTTON -->
      <v-flex xs12>
        <v-btn :disabled="submitButtonValidationStrategy()" name="loginSubmit" block color="primary" :loading="view.action.login.busy" @submit="logIn()" @click="logIn()">
          {{ $t("view.login.signin.submit") }}
        </v-btn>
      </v-flex>

      <!-- RESET PASSWORD LINK -->
      <v-flex xs12 class="py-3">
        <router-link :class="'signin--reset-pwd-link'" :to="{ name: 'ForgetPassword' }" :disabled="view.action.login.busy">
          <small>{{ $t("view.login.signin.forgetpwd") }}</small>
        </router-link>
      </v-flex>
    </v-layout>
  </form>
</template>

<script>
import Auth from "@/services/auth";
import { AuthenticationModel } from "@/models/authentication";
import { valueIsWithin } from "@/helpers/validators";
import { required, minLength } from "vuelidate/lib/validators";
import { isBooleanValue, alphaNumDot } from "@/helpers/vuelidate/validators";
import { allValidatorMethods } from "@/helpers/vuelidate";
import * as actions from "@/constants/mutation-names";
import transform from "@/helpers/transformations";
import motionCaptcha from "@/helpers/motionCaptcha";

const label = {
  loginError: {
    visible: false
    , text: ""
  }
};
const button = { login: { enable: true } };

const action = { login: { busy: false } };

// Strategies
function submitButtonValidationLoginStrategy() {
  return this.hasErrors(this.$v.auth);
}
function submitButtonValidationCaptchaStrategy() {
  this.$v.captcha.$touch();
  return this.hasErrors(this.$v.captcha) || this.hasErrors(this.$v.auth);
}

// Validation strategies
function loginValidationStrategy() {
  this.$v.auth.$touch();
  return this.isAllValid(this.$v.auth);
}
function captchaValidationStrategy() {
  this.captcha.value = !this.view.elem.captcha.domElement.disabled;
  let isValid = this.isAllValid(this.$v.auth) && this.isAllValid(this.$v.captcha);
  if (isValid) {
    this.resetValidationStrategy();
    this.view.elem.captcha.domElement.visible = false;
    this.captcha.value = false;
    this.view.elem.captcha.canvas.classList = "";
    this.$v.captcha.$reset();
  }
  return isValid;
}

// Vue Instance
export default {
  name: "SignInModule"
  , data() {
    return {
      auth: undefined
      , show: false
      , captcha: { value: false }
      , view: {
        title: this.$t("view.login.signin.tabtitle")
        , action
        , elem: {
          label
          , button
          , captcha: {
            domElement: undefined
            , canvas: undefined
            , visible: false
          }
        }
      }
      , validationStrategy: loginValidationStrategy
      , submitButtonValidationStrategy: submitButtonValidationLoginStrategy
    };
  }
  , head: {
    title: function () {
      return {
        inner: this.view.title
        , separator: "|"
        , complement: this.$t("view.commons.system")
      };
    }
  }
  , computed: {
    loginErrorMessage() {
      return this.view.elem.label.loginError.text;
    }
  }
  , created() {
    this.setInitialState();
  }
  , methods: {
    ...allValidatorMethods
    , setValidationStrategy(strategy) {
      this.validationStrategy = strategy;
      this.setSubmitButtonValidationStrategy(submitButtonValidationCaptchaStrategy);
    }
    , setSubmitButtonValidationStrategy(strategy) {
      this.submitButtonValidationStrategy = strategy;
    }
    , resetValidationStrategy() {
      this.validationStrategy = loginValidationStrategy;
      this.view.elem.captcha.visible = false;
      this.resetSubmitButtonValidationStrategy();
    }
    , resetSubmitButtonValidationStrategy() {
      this.submitButtonValidationStrategy = submitButtonValidationLoginStrategy;
    }
    , validateForm() {
      return this.validationStrategy();
    }
    , async logIn() {
      try {
        // Hide any previous error
        this.hideErrorLogIn();
        // Validate all models before make the request
        if (!this.validateForm()) {
          return;
        }
        // Show busy skeleton
        this.view.action.login.busy = true;
        // Call API Login
        const response = await Auth.logIn(this.auth);
        this.view.action.login.busy = false;
        let { data: { results: { token: userToken, usuario, roles } } } = response;
        // Transform data for store login action
        const data = { ...transform.ResponseToUserStore(usuario), userToken, roles };
        // Saving data state on store
        this.$store.dispatch(actions.AUTH_LOGIN, data);
        this.$userPermission.set({ roles });
        // Login success, route to home
        this.$router.push("/home");
      } catch (error) {
        // console.log(error)
        // Finish busy flag for login action
        this.view.action.login.busy = false;
        // Handling error protocols
        if (error && (error.status == "500" || error.status == "504" || error.status == "403")) {
          this.showErrorLogIn(this.$t(`errors.network.${error.status}.desc`));
          return;
        }
        // Extract errorCode
        const { data: { meta: { code: errorCode } } } = error;

        const login = this.$t("view.login");

        // List of errors required for login flow
        const errorsAPILogin = Object.keys(login.auth);
        const errorsExpiredPassword = Object.keys(login.expiredPassword);

        // Login's error
        if (valueIsWithin(errorsAPILogin, errorCode)) this.showErrorLogIn(this.$t(`view.login.auth.${errorCode}.text`));
        // Password Expired's error
        if (valueIsWithin(errorsExpiredPassword, errorCode)) {
          const { data: { results: { usuario: { nombre } } } } = error;
          this.$router.push({ name: "ExpiredPwdMessage", params: { username: this.auth.username, nombre: nombre } });
        }
        // Captcha error handler INVALID_PASSWORD_CAPTCHA = SC0012
        if (errorCode == "SC0012") {
          this.setValidationStrategy(captchaValidationStrategy);
          this.setInitialCaptchaState();
        }
      }
    }
    , showErrorLogIn(message) {
      this.view.elem.label.loginError.text = message;
      this.view.elem.label.loginError.visible = true;
    }
    , hideErrorLogIn() {
      if (!this.view.elem.label.loginError.visible) return;
      this.view.elem.label.loginError.text = "";
      this.view.elem.label.loginError.visible = false;
    }
    , logOut() {
      this.$store.dispatch(actions.AUTH_LOGOUT);
    }
    , routeToViewExpiredPassword() {
      this.$router.push({ path: "/expiredpwd", query: { username: this.auth.username } });
    }
    , setInitialState() {
      this.auth = new AuthenticationModel();
      this.view.elem.label.loginError.visible = false;
      this.view.elem.label.loginError.text = "";
    }
    , setInitialCaptchaState() {
      this.view.elem.captcha.domElement = document.getElementById("captchaResult");

      motionCaptcha.motionCaptcha("#authForm", {
        errorMsg: String.fromCharCode(parseInt("2715", 16))
        , successMsg: String.fromCharCode(parseInt("2713", 16))
        , submitId: "#captchaResult"
        , actionId: "#captchaResult"
        , canvasFont: "13px \"open sans\", \"Helvetica Neue\", \"Helvetica\", \"Arial\", \"sans-serif\""
      });

      this.view.elem.captcha.visible = true;
      // keep reference of captcha element
      const captchaValue = this.captcha;
      const captchaResult = this.view.elem.captcha.domElement;
      // keep reference of canvas element
      this.view.elem.captcha.canvas = document.getElementById("mc-canvas");
      this.view.elem.captcha.canvas.addEventListener(
        "click",
        function () {
          captchaValue.value = !captchaResult.disabled;
        },
        false
      );
    }
  }
  , validations: {
    auth: {
      username: {
        required
        , alphaNumDot
      }
      , password: {
        required
        , minLength: minLength(8)
      }
    }
    , captcha: { value: { isBooleanValue: isBooleanValue(true) } }
  }
};
</script>
<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.signin-container {
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/views/authentication/SignIn.vue